import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';

import { useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { merge } from 'lodash-es';

import { IBaseProps } from '@rbi-ctg/frontend';
import { MenuObject, SanityMenuObject } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus, MenuObjectTypes } from 'enums/menu';
import {
  GetComboAvailabilityDocument,
  GetComboDocument,
  GetItemAvailabilityDocument,
  GetItemDocument,
  GetPickerAvailabilityDocument,
  GetPickerDocument,
  GetSectionAvailabilityDocument,
  GetSectionDocument,
} from 'generated/sanity-graphql';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useForceUpdate } from 'hooks/use-force-update';
import { fixTypes } from 'remote/api/menu-objects';
import { MenuDataError, NotFoundMenuItemError } from 'remote/exceptions';
import { useDayPartContext } from 'state/day-part';
import { useLocale } from 'state/intl';
import { useStoreContext } from 'state/store';
import { usePosDataQuery } from 'state/store/hooks/use-pos-data-query';
import { isAvailableForActiveDayParts } from 'utils/availability';
import { useSanityGqlEndpoint } from 'utils/network';
import { PosVendors } from 'utils/vendor-config';

import { applyFiltersForAvailability } from './apply-filters-for-availability';
import { IUsePricingFunctionHookReturnType } from './hooks/types';
import { usePricingFunction } from './hooks/use-pricing-function';
import { remapItemOptions } from './remap-item-options';

interface ICheckItemAvailability {
  vendor: PosVendors | null;
  itemId: string;
  restaurantPosDataId: string;
  storeNumber: string;
}

export type ItemAvailability = {
  availabilityStatus: ItemAvailabilityStatus;
  data?: MenuObject;
};

export type IMenuOptionsContext = {
  getMenuObject(id: string): IState;
  asyncGetMenuObject(id: string, customFilter?: ICustomFilter): Promise<IState>;
  asyncGetRawMenuObject(id: string): Promise<IState['data']>;
  filterForAvailability(
    menuData: SanityMenuObject[] | SanityMenuObject
  ): MenuObject | MenuObject[] | null;
  checkItemAvailability(args: ICheckItemAvailability): Promise<ItemAvailability>;
} & IUsePricingFunctionHookReturnType;

export interface IState {
  data: MenuObject | null;
  loading: boolean;
}
interface ICustomFilter {
  (data: SanityMenuObject): MenuObject | MenuObject[] | null;
}

export const MenuOptionsContext = createContext<IMenuOptionsContext>({} as IMenuOptionsContext);
export const useMenuOptionsContext = () => useContext(MenuOptionsContext);

export default MenuOptionsContext.Consumer;

export const MenuOptionsProvider = ({ children }: IBaseProps) => {
  const { activeDayParts, dayParts } = useDayPartContext();
  const { prices, store } = useStoreContext();
  const { vendor: posVendor } = usePosVendor();
  const menu = useRef<Record<string, IState>>({});
  const { locale } = useLocale();
  const client = useApolloClient();
  const uri = useSanityGqlEndpoint();
  const { refetch: getPosData } = usePosDataQuery({
    storeNumber: null,
    restaurantPosDataId: null,
    lazy: true,
  });

  // a small hack until our menu data store is more reactive
  // forces a re-render upon successful menu data load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const forceUpdate = useCallback(useForceUpdate(), [useForceUpdate]);

  const filterForAvailability = React.useMemo(
    () =>
      applyFiltersForAvailability({
        activeDayParts,
        dayParts,
        prices,
        vendor: posVendor,
      }),
    [activeDayParts, dayParts, prices, posVendor]
  );

  const filterForAvailabilityMemo = useCallback(filterForAvailability, [filterForAvailability]);

  const {
    priceForItemOptionModifier,
    priceForComboSlotSelection,
    pricingFunction,
    priceForItemInComboSlotSelection,
  } = usePricingFunction({
    prices,
    vendor: posVendor,
  });

  const setMenuData = useCallback(
    (
      menuData: any,
      menuDataLoading: any,
      itemID: any,
      skipForceUpdate = false,
      customFilter?: ICustomFilter
    ) => {
      const fixedData = menuData && fixTypes(menuData);
      const filter = customFilter || filterForAvailabilityMemo;
      const filteredData = fixedData && filter(fixedData);

      let sectionOptions = {};
      // if its a section we can pull out the pickers, combos and items to preload the data into their own keys (This saves wizard from having to make the same queries again)
      // NOTE: Checking if filteredData is an array here as a typecheck... We should never hit that case here. I am assuming its because the filter function is used somewhere else and could return an array of data
      if (!Array.isArray(filteredData) && filteredData?._type === MenuObjectTypes.SECTION) {
        // typecasting as the filterForStaticMenu returns sanity types while applyFiltersForAvailability returns manual menu type
        sectionOptions = (filteredData.options as MenuObject[]).reduce(
          (mappedOptions: any, option) => {
            return {
              ...mappedOptions,
              [option._id]: {
                loading: menuDataLoading,
                data: option,
                id: option._id,
              },
            };
          },
          {}
        );
      }

      menu.current = {
        ...menu.current,
        // preload all the pickers, combos, and items inside a section so we don't need to query again for them
        ...sectionOptions,
        [itemID]: {
          loading: menuDataLoading,
          data: filteredData,
          id: itemID,
        },
      };
      if (!skipForceUpdate) {
        forceUpdate();
      }
    },
    [filterForAvailabilityMemo, forceUpdate]
  );

  const getItemIdAndType = useCallback((id: string) => {
    const [type, ...idSplit] = id.split('-');
    const itemID = idSplit.join('-');
    return { type, itemID };
  }, []);

  const maybeGetCurrentItem = useCallback(
    (itemID: string, customFilter?: ICustomFilter) => {
      const { current: currentMenu } = menu;

      const filter = customFilter || filterForAvailabilityMemo;

      if (currentMenu[itemID]) {
        currentMenu[itemID] = {
          ...currentMenu[itemID],
          data:
            currentMenu[itemID].data && filter(currentMenu[itemID].data as SanityMenuObject)
              ? currentMenu[itemID]?.data
              : null,
        };

        return currentMenu[itemID];
      }
      return undefined;
    },
    [filterForAvailabilityMemo]
  );
  /**
   *  id is a bit of a misnomer. This is the actually
   *  `${MenuObjectTypes}-${sanityId}`
   *  A CartEntryType can be mapped to MenuObjectTypes
   *  by transforming to camelCase
   *  e.g ComboSlot -> comboSlot
   */
  const getMenuObject = useCallback(
    (id: string) => {
      const { type, itemID } = getItemIdAndType(id);
      const currentMenuItem = maybeGetCurrentItem(itemID);
      if (currentMenuItem) {
        return currentMenuItem;
      }

      const queryMenuData = (
        menuItemId: string,
        dataQuery: DocumentNode,
        availabilityQuery: DocumentNode
      ) => {
        Promise.all([
          client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query: dataQuery,
            variables: {
              id: menuItemId,
            },
          }),
          client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query: availabilityQuery,
            variables: {
              id: menuItemId,
            },
          }),
        ])
          .then(response => {
            const { data: rawData, loading } = response.reduce(
              // @ts-expect-error TS(2769) FIXME: No overload matches this call.
              (finalResponse, res) => {
                const capitalizedType = type[0].toUpperCase() + type.slice(1);
                const data = res?.data;

                return {
                  data: data?.[capitalizedType]
                    ? [...finalResponse.data, data[capitalizedType]]
                    : finalResponse.data,
                  loading: res.loading || finalResponse.loading,
                };
              },
              { data: [], loading: false }
            );
            const [uiData, availability] = rawData;

            const mergedData = merge(uiData, availability);
            setMenuData(mergedData, loading, menuItemId);
          })
          .catch(err => {
            throw new MenuDataError(`An error occurred loading menu ${type}`, err);
          });
      };

      // Set as loading to prevent simultaneous calls
      setMenuData(null, true, itemID, true);

      switch (type) {
        case MenuObjectTypes.ITEM:
          queryMenuData(itemID, GetItemDocument, GetItemAvailabilityDocument);
          break;
        case MenuObjectTypes.COMBO:
          queryMenuData(itemID, GetComboDocument, GetComboAvailabilityDocument);
          break;
        case MenuObjectTypes.PICKER:
          queryMenuData(itemID, GetPickerDocument, GetPickerAvailabilityDocument);
          break;
        case MenuObjectTypes.SECTION:
          queryMenuData(itemID, GetSectionDocument, GetSectionAvailabilityDocument);
          break;
        default:
          throw new NotFoundMenuItemError(type);
      }

      return menu.current[itemID] || { loading: true, data: null };
    },
    [getItemIdAndType, maybeGetCurrentItem, client, uri, setMenuData]
  );

  /**
   * Like getMenuObject but async instead of reactive
   */
  const asyncGetMenuObject = useCallback(
    async (id: string, customFilter?: ICustomFilter) => {
      const { type, itemID } = getItemIdAndType(id);
      const currentMenuItem = maybeGetCurrentItem(itemID, customFilter);
      if (currentMenuItem) {
        return currentMenuItem;
      }
      const queryMenuData = async (
        menuItemId: string,
        dataQuery: DocumentNode,
        availabilityQuery: DocumentNode
      ) => {
        await Promise.all([
          client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query: dataQuery,
            variables: {
              id: menuItemId,
            },
          }),
          client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query: availabilityQuery,
            variables: {
              id: menuItemId,
            },
          }),
        ])
          .then(response => {
            const { data: rawData, loading } = response.reduce(
              // @ts-expect-error TS(2769) FIXME: No overload matches this call.
              (finalResponse, res) => {
                const capitalizedType = type[0].toUpperCase() + type.slice(1);
                const data = res?.data;

                return {
                  data: data?.[capitalizedType]
                    ? [...finalResponse.data, data[capitalizedType]]
                    : finalResponse.data,
                  loading: res.loading || finalResponse.loading,
                };
              },
              { data: [], loading: false }
            );
            const [uiData, availability] = rawData;

            const mergedData = merge(uiData, availability);
            setMenuData(mergedData, loading, menuItemId, true, customFilter);
          })
          .catch(err => {
            throw new MenuDataError(`An error occurred loading menu ${type}`, err);
          });
      };
      switch (type) {
        case MenuObjectTypes.ITEM:
          await queryMenuData(itemID, GetItemDocument, GetItemAvailabilityDocument);
          break;
        case MenuObjectTypes.COMBO:
          await queryMenuData(itemID, GetComboDocument, GetComboAvailabilityDocument);
          break;
        case MenuObjectTypes.PICKER:
          await queryMenuData(itemID, GetPickerDocument, GetPickerAvailabilityDocument);
          break;
        case MenuObjectTypes.SECTION:
          await queryMenuData(itemID, GetSectionDocument, GetSectionAvailabilityDocument);
          break;
        default:
          throw new NotFoundMenuItemError(type);
      }

      return menu.current[itemID];
    },
    [client, getItemIdAndType, maybeGetCurrentItem, setMenuData, uri]
  );

  /**
   * Like asyncGetMenuObject but will always fire a network request instead of using reference (network responses are cached by the browser)
   */
  const asyncGetRawMenuObject = useCallback(
    async (id: string) => {
      const { type, itemID } = getItemIdAndType(id);
      const queryMenuData = async (
        menuItemId: string,
        dataQuery: DocumentNode,
        availabilityQuery: DocumentNode
      ) => {
        return Promise.all([
          client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query: dataQuery,
            variables: {
              id: menuItemId,
            },
          }),
          client.query({
            fetchPolicy: 'no-cache',
            context: { uri },
            query: availabilityQuery,
            variables: {
              id: menuItemId,
            },
          }),
        ])
          .then(response => {
            const { data: rawData } = response.reduce(
              // @ts-expect-error TS(2769) FIXME: No overload matches this call.
              (finalResponse, res) => {
                const capitalizedType = type[0].toUpperCase() + type.slice(1);
                const data = res?.data;

                return {
                  data: data?.[capitalizedType]
                    ? [...finalResponse.data, data[capitalizedType]]
                    : finalResponse.data,
                  loading: res.loading || finalResponse.loading,
                };
              },
              { data: [], loading: false }
            );
            const [uiData, availability] = rawData;

            const mergedData = merge(uiData, availability);
            const fixedData = mergedData ? fixTypes(mergedData) : null;
            return remapItemOptions(fixedData) as MenuObject;
          })
          .catch(err => {
            throw new MenuDataError(`An error occurred loading menu ${type}`, err);
          });
      };

      switch (type) {
        case MenuObjectTypes.ITEM:
          return queryMenuData(itemID, GetItemDocument, GetItemAvailabilityDocument);
        case MenuObjectTypes.COMBO:
          return queryMenuData(itemID, GetComboDocument, GetComboAvailabilityDocument);
        case MenuObjectTypes.PICKER:
          return queryMenuData(itemID, GetPickerDocument, GetPickerAvailabilityDocument);
        case MenuObjectTypes.SECTION:
          return queryMenuData(itemID, GetSectionDocument, GetSectionAvailabilityDocument);
        default:
          throw new NotFoundMenuItemError(type);
      }
    },
    [client, getItemIdAndType, uri]
  );

  const checkItemAvailability: IMenuOptionsContext['checkItemAvailability'] = useCallback(
    async ({
      vendor,
      itemId,
      restaurantPosDataId,
      storeNumber,
    }: ICheckItemAvailability): Promise<ItemAvailability> => {
      try {
        const posDataResponse = await getPosData({ restaurantPosDataId, storeNumber });
        if (!posDataResponse) {
          return { availabilityStatus: ItemAvailabilityStatus.UNAVAILABLE };
        }

        const isItemAvailable = async (plus: Record<string, number>) => {
          const availabilityFilter = applyFiltersForAvailability({
            activeDayParts,
            dayParts,
            prices: plus,
            vendor,
          });
          const { data } = await asyncGetMenuObject(itemId, availabilityFilter);

          if (
            dayParts.length &&
            activeDayParts.length &&
            !isAvailableForActiveDayParts({ activeDayParts, menuData: data })
          ) {
            return {
              availabilityStatus: ItemAvailabilityStatus.OUT_OF_DAYPART,
              data: data ?? undefined,
            };
          }

          const optionsAvailableForPicker = (menuData: MenuObject): ItemAvailability => {
            // If selected item is picker, make sure there are at least some options available.
            const notPicker = menuData?._type !== MenuObjectTypes.PICKER;
            if (notPicker || Boolean(menuData?.options?.length)) {
              return {
                availabilityStatus: ItemAvailabilityStatus.AVAILABLE,
                data: data ?? undefined,
              };
            }

            return {
              availabilityStatus: ItemAvailabilityStatus.OUT_OF_MENU,
              data: data ?? undefined,
            };
          };

          return data
            ? optionsAvailableForPicker(data)
            : { availabilityStatus: ItemAvailabilityStatus.OUT_OF_MENU, data: data ?? undefined };
        };

        return posDataResponse.posData
          ? isItemAvailable(posDataResponse.posData)
          : { availabilityStatus: ItemAvailabilityStatus.UNAVAILABLE };
      } catch (err) {
        throw new NotFoundMenuItemError(itemId);
      }
    },
    [activeDayParts, asyncGetMenuObject, dayParts, getPosData]
  );

  // There are a few specs that cause us to force remove all items.
  //
  // 1. When the store changes, we have to remove all loaded items.
  //    This is because a store might have different menu data.
  // 2. When then language changes, we wipe the data so that the user
  //    refetches the data and then gets it in the new language
  // 3. When the prices are refetched, we clear all loaded items since
  //    the filtered data will be completely different
  useEffect(() => {
    menu.current = {};
  }, [locale, store, prices]);

  return (
    <MenuOptionsContext.Provider
      value={{
        // NOTE!! this cannot be memoized properly because `menu` is a ref (rather than state) and a force update is used
        // i tried migrating `menu` from a ref to state and other issues occurred
        getMenuObject,
        priceForItemOptionModifier,
        pricingFunction,
        priceForComboSlotSelection,
        priceForItemInComboSlotSelection,
        filterForAvailability,
        asyncGetMenuObject,
        asyncGetRawMenuObject,
        checkItemAvailability,
      }}
    >
      {children}
    </MenuOptionsContext.Provider>
  );
};
